import React from 'react';
import clsx from 'clsx';
import {
    FileSpreadsheet,
    CheckSquare,
    Square,
    ChevronUp,
    ChevronDown,
    Send,
    CheckCircle,
    XCircle,
    History,
    Maximize2,
} from 'lucide-react';
import { PfmeaItem } from '../../types';
import {
    EditableField,
    STATUS_STYLES,
    getInputComponent,
    getDisplayComponent,
} from '../PfmeaEditorHelpers';
import { PfmeaLayoutSettings } from './LayoutSettingsPanel';

interface PfmeaTableProps {
    items: PfmeaItem[];
    filteredItems: PfmeaItem[];
    loading: boolean;
    isDark: boolean;
    selectedIds: Set<string>;
    sortField: keyof PfmeaItem;
    sortDirection: 'asc' | 'desc';
    toggleSelectAll: () => void;
    handleSort: (field: keyof PfmeaItem) => void;
    toggleSelect: (id: string) => void;

    // For cell rendering
    editingCell: { id: string; field: EditableField } | null;
    pendingChanges: Record<string, Partial<PfmeaItem>>;
    handleCellClick: (id: string, field: EditableField) => void;
    handleCellChange: (id: string, field: EditableField, value: any) => void;
    handleCellBlur: () => void;

    // Actions
    hasPermission: (p: string) => boolean;
    handleSubmitForReview: (id: string) => void;
    handleApprove: (id: string) => void;
    handleReject: (id: string) => void;
    handleViewHistory: (item: PfmeaItem) => void;

    calculateRPN: (s: number, o: number, d: number) => number;
    handleCellExpand?: (id: string, field: EditableField) => void;
    layoutSettings?: PfmeaLayoutSettings;
}

const PfmeaTable: React.FC<PfmeaTableProps> = ({
    items,
    filteredItems,
    loading,
    isDark,
    selectedIds,
    sortField,
    sortDirection,
    toggleSelectAll,
    handleSort,
    toggleSelect,
    editingCell,
    pendingChanges,
    handleCellClick,
    handleCellChange,
    handleCellBlur,
    hasPermission,
    handleSubmitForReview,
    handleApprove,
    handleReject,
    handleViewHistory,
    calculateRPN,
    handleCellExpand,
    layoutSettings,
}) => {
    const wrapText = layoutSettings?.wrapText ?? true;
    const colorMode = layoutSettings?.colorMode ?? 'none';
    const showCellBorders = layoutSettings?.showCellBorders ?? false;
    const hiddenColumns = layoutSettings?.hiddenColumns ?? new Set<string>();
    const riskRanges = layoutSettings?.riskRanges ?? { red: { min: 7, max: 10 }, yellow: { min: 4, max: 6 }, green: { min: 1, max: 3 } };

    // Row color based on colorMode
    const getRowClass = (item: PfmeaItem, idx: number): string => {
        const maxRating = Math.max(item.severity ?? 0, item.occurrence ?? 0, item.detection ?? 0);
        if (colorMode === 'risk-traffic-light') {
            if (maxRating >= riskRanges.red.min && maxRating <= riskRanges.red.max)
                return 'bg-red-50 dark:bg-red-900/10 hover:bg-red-100/50';
            if (maxRating >= riskRanges.yellow.min && maxRating <= riskRanges.yellow.max)
                return 'bg-yellow-50 dark:bg-yellow-900/10 hover:bg-yellow-100/50';
            if (maxRating >= riskRanges.green.min && maxRating <= riskRanges.green.max)
                return 'bg-green-50 dark:bg-green-900/10 hover:bg-green-100/50';
            return isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50';
        }
        if (colorMode === 'alt-gray')
            return idx % 2 === 0
                ? (isDark ? 'bg-slate-900 hover:bg-slate-800' : 'bg-white hover:bg-slate-50')
                : (isDark ? 'bg-slate-800/40 hover:bg-slate-800' : 'bg-slate-50 hover:bg-slate-100/60');
        if (colorMode === 'alt-blue')
            return idx % 2 === 0
                ? (isDark ? 'bg-slate-900 hover:bg-blue-900/10' : 'bg-white hover:bg-blue-50/30')
                : (isDark ? 'bg-blue-900/10 hover:bg-blue-900/20' : 'bg-blue-50/50 hover:bg-blue-100/40');
        if (colorMode === 'alt-green-white')
            return idx % 2 === 0
                ? (isDark ? 'bg-slate-900 hover:bg-emerald-900/10' : 'bg-white hover:bg-emerald-50/30')
                : (isDark ? 'bg-emerald-900/10 hover:bg-emerald-900/20' : 'bg-emerald-50/40 hover:bg-emerald-100/40');
        return isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50';
    };

    const borderCls = showCellBorders ? 'border border-slate-300 dark:border-slate-600' : '';
    const tdCls = `px-2 py-2 overflow-hidden ${borderCls}`;

    // Render helpers - using strategy pattern from extracted helpers
    const renderEditableCell = (item: PfmeaItem, field: EditableField, width?: string) => {
        if (hiddenColumns.has(field)) return null;
        const isEditing = editingCell?.id === item.id && editingCell?.field === field;
        const value = (item as any)[field];
        const hasPendingChange = pendingChanges[item.id]?.[field] !== undefined;

        // Determine if this is an expandable text field
        const expandableFields: EditableField[] = [
            'processStepName', 'processFunction', 'failureMode',
            'failureEffectEnd', 'failureCause', 'preventionControls', 'detectionControls'
        ];
        const isExpandable = expandableFields.includes(field);

        if (isEditing) {
            // Use strategy pattern to get the appropriate input component
            const InputComponent = getInputComponent(field);
            return (
                <InputComponent
                    value={value}
                    onChange={(newValue) => handleCellChange(item.id, field, newValue)}
                    onBlur={handleCellBlur}
                />
            );
        }

        // Display mode - use strategy pattern for display component
        const DisplayComponent = getDisplayComponent(field);
        const displayValue = <DisplayComponent value={value} />;

        return (
            <div
                className={clsx(
                    'cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 px-2 py-1 rounded transition-colors min-h-[28px] overflow-hidden relative group',
                    hasPendingChange && 'bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-500'
                )}
                style={{ width, maxWidth: width }}
            >
                <div
                    onClick={() => handleCellClick(item.id, field)}
                    className={wrapText ? 'whitespace-pre-wrap break-words' : 'truncate'}
                >
                    {displayValue}
                </div>
                {/* Expand button for text fields */}
                {isExpandable && handleCellExpand && (
                    <button
                        onClick={(e) => { e.stopPropagation(); handleCellExpand(item.id, field); }}
                        className="absolute right-0.5 top-0.5 p-0.5 text-slate-400 hover:text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 dark:bg-slate-800/80 rounded"
                        title="Expand to read & edit full text"
                    >
                        <Maximize2 size={12} />
                    </button>
                )}
            </div>
        );
    };


    return (
        <div className={clsx(
            'rounded-xl border overflow-hidden',
            isDark ? 'border-slate-700' : 'border-slate-200'
        )}>
            <div className="overflow-x-auto">
                <table
                    className={clsx('w-full text-sm', wrapText ? 'table-auto' : 'table-fixed')}
                    style={{ borderCollapse: showCellBorders ? 'collapse' : 'separate', borderSpacing: 0 }}
                >
                    <thead className={clsx(isDark ? 'bg-slate-800' : 'bg-slate-50')}>
                        <tr>
                            <th className="px-3 py-3 w-10">
                                <button onClick={toggleSelectAll} className="hover:text-primary-500">
                                    {selectedIds.size === filteredItems.length && filteredItems.length > 0
                                        ? <CheckSquare size={16} />
                                        : <Square size={16} />
                                    }
                                </button>
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase cursor-pointer hover:text-primary-500 w-[70px]" onClick={() => handleSort('processStepNumber')}>
                                Step # {sortField === 'processStepNumber' && (sortDirection === 'asc' ? <ChevronUp size={14} className="inline" /> : <ChevronDown size={14} className="inline" />)}
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-[140px]">Process Step</th>
                            <th className="px-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-[70px]">4M</th>
                            <th className="px-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-[180px]">Failure Mode</th>
                            <th className="px-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-[180px]">Effect</th>
                            <th className="px-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-[180px]">Cause</th>
                            <th className="px-2 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-[40px]">S</th>
                            <th className="px-2 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-[55px]">Class</th>
                            <th className="px-2 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-[40px]">O</th>
                            <th className="px-2 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-[40px]">D</th>
                            <th className="px-2 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-[50px]">RPN</th>
                            <th className="px-2 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-[40px]">AP</th>
                            <th className="px-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-[160px]">Prevention</th>
                            <th className="px-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-[160px]">Detection</th>
                            <th className="px-2 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-[70px]">Status</th>
                            <th className="px-2 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-[80px]">Actions</th>
                        </tr>
                    </thead>
                    <tbody className={isDark ? 'bg-slate-900' : 'bg-white'}>
                        {loading ? (
                            <tr>
                                <td colSpan={17} className="px-4 py-12 text-center text-slate-500">
                                    Loading...
                                </td>
                            </tr>
                        ) : filteredItems.length === 0 ? (
                            <tr>
                                <td colSpan={17} className="px-4 py-12 text-center">
                                    <FileSpreadsheet className="mx-auto text-slate-300 mb-4" size={48} />
                                    <p className={clsx('text-lg font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                                        No PFMEA items found
                                    </p>
                                    <p className="text-sm text-slate-500">
                                        {items.length === 0
                                            ? 'Start by adding items or generate with AI.'
                                            : 'Try adjusting your filters.'
                                        }
                                    </p>
                                </td>
                            </tr>
                        ) : (
                            filteredItems.map((item, idx) => {
                                const rpn = calculateRPN(item.severity, item.occurrence, item.detection);
                                const rpnColor = rpn >= 100 ? 'text-red-500 font-bold' : rpn >= 50 ? 'text-amber-500' : 'text-green-500';
                                const rowBg = getRowClass(item, idx);

                                return (
                                    <tr
                                        key={item.id}
                                        className={clsx(
                                            'border-t transition-colors',
                                            isDark ? 'border-slate-700' : 'border-slate-100',
                                            selectedIds.has(item.id) && (isDark ? 'bg-primary-900/20' : 'bg-primary-50'),
                                            !selectedIds.has(item.id) && rowBg
                                        )}
                                    >
                                        <td className="px-3 py-2">
                                            <button onClick={() => toggleSelect(item.id)} className="hover:text-primary-500">
                                                {selectedIds.has(item.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                                            </button>
                                        </td>
                                        <td className={tdCls}>{renderEditableCell(item, 'processStepNumber', '60px')}</td>
                                        <td className={tdCls}>{renderEditableCell(item, 'processStepName', '130px')}</td>
                                        <td className={tdCls}>{renderEditableCell(item, 'processElement', '60px')}</td>
                                        <td className={tdCls}>{renderEditableCell(item, 'failureMode', '170px')}</td>
                                        <td className={tdCls}>{renderEditableCell(item, 'failureEffectEnd', '170px')}</td>
                                        <td className={tdCls}>{renderEditableCell(item, 'failureCause', '170px')}</td>
                                        <td className={`px-2 py-2 text-center ${borderCls}`}>{renderEditableCell(item, 'severity', '35px')}</td>
                                        <td className={`px-2 py-2 text-center ${borderCls}`}>{renderEditableCell(item, 'specialCharacteristic', '45px')}</td>
                                        <td className={`px-2 py-2 text-center ${borderCls}`}>{renderEditableCell(item, 'occurrence', '35px')}</td>
                                        <td className={`px-2 py-2 text-center ${borderCls}`}>{renderEditableCell(item, 'detection', '35px')}</td>
                                        {!hiddenColumns.has('rpn') && <td className={clsx('px-2 py-2 text-center font-mono', rpnColor, borderCls)}>{rpn}</td>}
                                        {!hiddenColumns.has('actionPriority') && (
                                            <td className={`px-2 py-2 text-center ${borderCls}`}>
                                                <span className={clsx(
                                                    'px-2 py-0.5 rounded text-xs font-bold',
                                                    item.actionPriority === 'H' ? 'bg-red-500 text-white' :
                                                        item.actionPriority === 'M' ? 'bg-amber-500 text-white' :
                                                            'bg-green-500 text-white'
                                                )}>
                                                    {item.actionPriority}
                                                </span>
                                            </td>
                                        )}
                                        <td className={tdCls}>{renderEditableCell(item, 'preventionControls', '150px')}</td>
                                        <td className={tdCls}>{renderEditableCell(item, 'detectionControls', '150px')}</td>
                                        {!hiddenColumns.has('status') && (
                                            <td className={`px-3 py-2 text-center ${borderCls}`}>
                                                <span className={clsx('px-2 py-0.5 rounded text-xs capitalize', STATUS_STYLES[item.status])}>
                                                    {item.status}
                                                </span>
                                            </td>
                                        )}
                                        <td className="px-3 py-2">
                                            <div className="flex items-center justify-center gap-1">
                                                {/* Submit for Review */}
                                                {item.status === 'draft' && hasPermission('pfmea.edit') && (
                                                    <button
                                                        onClick={() => handleSubmitForReview(item.id)}
                                                        className="p-1 text-blue-500 hover:bg-blue-100 rounded"
                                                        title="Submit for Review"
                                                    >
                                                        <Send size={14} />
                                                    </button>
                                                )}
                                                {/* Approve */}
                                                {item.status === 'review' && hasPermission('pfmea.approve') && (
                                                    <button
                                                        onClick={() => handleApprove(item.id)}
                                                        className="p-1 text-green-500 hover:bg-green-100 rounded"
                                                        title="Approve"
                                                    >
                                                        <CheckCircle size={14} />
                                                    </button>
                                                )}
                                                {/* Reject */}
                                                {item.status === 'review' && hasPermission('pfmea.approve') && (
                                                    <button
                                                        onClick={() => handleReject(item.id)}
                                                        className="p-1 text-red-500 hover:bg-red-100 rounded"
                                                        title="Reject"
                                                    >
                                                        <XCircle size={14} />
                                                    </button>
                                                )}
                                                {/* History */}
                                                <button
                                                    onClick={() => handleViewHistory(item)}
                                                    className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                                                    title="View History"
                                                >
                                                    <History size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer */}
            {filteredItems.length > 0 && (
                <div className={clsx(
                    'px-4 py-3 border-t flex items-center justify-between text-sm',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                )}>
                    <span className="text-slate-500">
                        Showing {filteredItems.length} of {items.length} items
                    </span>
                    <div className="flex items-center gap-4">
                        <span className="text-slate-500">
                            High: <span className="font-bold text-red-500">{filteredItems.filter(i => i.actionPriority === 'H').length}</span>
                        </span>
                        <span className="text-slate-500">
                            Medium: <span className="font-bold text-amber-500">{filteredItems.filter(i => i.actionPriority === 'M').length}</span>
                        </span>
                        <span className="text-slate-500">
                            Low: <span className="font-bold text-green-500">{filteredItems.filter(i => i.actionPriority === 'L').length}</span>
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PfmeaTable;
